<template>
  <div>
    <vue-header />
    <div class="wd-container" style="margin-top: 15px;text-align:left;">
      <div class="container">
        <loading :active.sync="isLoading" :can-cancel="false" :width="50" :height="50" :is-full-page="false">
        </loading>

        <h4 class="page-title">Assignments</h4>

        <b-card no-body dark>
          <b-tabs pills card>
            <b-tab title="Future Assignments" @click="loadAssignments('future')" active>
              <b-card-text>
                <div class="action-buttons mb-3">
                  <button type="button" class="btn btn-info btn-sm mr-2" @click="expandAll">
                    <i class="fa fa-expand"></i> Expand All
                  </button>
                  <button type="button" class="btn btn-secondary btn-sm mr-2" @click="collapseAll">
                    <i class="fa fa-compress"></i> Collapse All
                  </button>
                  <button type="button" class="btn btn-success btn-sm" @click="loadAssignments('future')">
                    <i class="fa fa-refresh"></i> Refresh
                  </button>
                </div>
                <div v-if="assignments.length === 0 && !isLoading" class="text-center py-4">
                  <p>No future assignments found.</p>
                </div>
                <div v-else class="assignments-list">
                  <div v-for="(assignment, index) in assignments" :key="assignment.id" class="assignment-row">
                    <div class="assignment-header" @click="toggleExpand(index)">
                      <div class="row">
                        <div class="col-3"><strong>{{ assignment.date_formatted }}</strong></div>
                        <div class="col-3">
                          <span v-if="getUserCustomTime(assignment)" style="color: #ff9800; font-weight: bold;">
                            <i class="fa fa-clock-o"></i> {{ getUserCustomTime(assignment) }}
                          </span>
                          <span v-else>
                            {{ assignment.call_time }}
                          </span>
                        </div>
                        <div class="col-5">{{ assignment.details }} ({{ assignment.status }})</div>
                        <div class="col-1 text-right">
                          <i :class="expandedRows.includes(index) ? 'fa fa-caret-down' : 'fa fa-caret-right'"></i>
                        </div>
                      </div>
                    </div>
                    <div v-if="expandedRows.includes(index)" class="assignment-details">
                      <div class="detail-section" v-if="assignment.people && assignment.people.length > 0">
                        <strong>People ({{ assignment.people.length }})</strong>
                        <ul class="detailed-list">
                          <li v-for="person in assignment.people" :key="person.id">
                            <div class="person-main-info">
                              {{ person.name }}
                              <template v-if="person.individual_start_time && person.individual_end_time">
                                - <strong>{{ formatTime(person.individual_start_time) }} - {{
                                  formatTime(person.individual_end_time) }}</strong>
                              </template>
                              <template v-if="person.email"> - {{ person.email }}</template>
                              <template v-if="person.phone"> - {{ person.phone }}</template>
                              <template v-if="person.mobile && person.mobile !== person.phone"> - {{ person.mobile
                              }}</template>
                            </div>
                            <ul v-if="person.info && person.info.Credentials && person.info.Credentials !== 'N/A'"
                              class="credentials-list">
                              <li v-for="(cred, idx) in parseCredentials(person.info.Credentials)" :key="idx">{{ cred }}
                              </li>
                            </ul>
                          </li>
                        </ul>
                      </div>
                      <div class="detail-section" v-if="assignment.assets && assignment.assets.length > 0">
                        <strong>Assets ({{ assignment.assets.length }})</strong>
                        <ul class="detailed-list">
                          <li v-for="asset in assignment.assets" :key="asset.id">{{ asset.alias || asset.name ||
                            asset.id }}</li>
                        </ul>
                      </div>
                      <div class="detail-section" v-if="assignment.jobs && assignment.jobs.length > 0">
                        <strong>Jobs ({{ assignment.jobs.length }})</strong>
                        <ul class="detailed-list">
                          <li v-for="job in assignment.jobs" :key="job.id">
                            <div class="job-name">{{ job.name }}</div>
                            <ul v-if="hasJobContacts(job)" class="contacts-list">
                              <li v-if="job.site_name" class="site-address-line">{{ job.site_name }}</li>
                              <li v-if="job.site_address_1" class="site-address-line">{{ job.site_address_1 }}</li>
                              <li v-if="job.site_address_2" class="site-address-line">{{ job.site_address_2 }}</li>
                              <li v-if="job.site_city || job.site_state || job.site_zip" class="site-address-line">{{
                                [job.site_city,
                                job.site_state, job.site_zip].filter(Boolean).join(', ') }}</li>
                              <li
                                v-if="job.people && job.people.primary_contact && job.people.primary_contact.full_name">
                                Primary Contact - {{ formatContactInfo(job.people.primary_contact) }}
                              </li>
                              <li v-if="job.people && job.people.estimator && job.people.estimator.full_name">
                                Estimator - {{ formatContactInfo(job.people.estimator) }}
                              </li>
                              <li
                                v-if="job.people && job.people.project_manager && job.people.project_manager.full_name">
                                Project Manager - {{ formatContactInfo(job.people.project_manager) }}
                              </li>
                              <li v-if="job.people && job.people.foreman && job.people.foreman.full_name">
                                Foreman - {{ formatContactInfo(job.people.foreman) }}
                              </li>
                              <li v-if="job.people && job.people.accountant && job.people.accountant.full_name">
                                Accountant - {{ formatContactInfo(job.people.accountant) }}
                              </li>
                            </ul>
                            <button class="btn btn-primary my-2" @click="openTimesheetModal(job)">Add Job To
                              Timesheet</button>
                          </li>
                        </ul>
                      </div>
                      <div class="detail-section" v-if="assignment.notes && assignment.notes.length > 0">
                        <strong>Crew Notes ({{ assignment.notes.length }})</strong>
                        <ul class="detailed-list notes-list">
                          <li v-for="note in assignment.notes" :key="note.id">
                            <div class="note-item">
                              <span v-if="note.activity" class="note-activity"><strong>{{ note.activity }}</strong>:
                              </span>
                              <span class="note-text">{{ note.full_note || note.text }}</span>
                            </div>
                            <div v-if="note.created_by_user || note.created_at" class="note-meta">
                              <span v-if="note.created_by_user">{{ note.created_by_user }}</span>
                              <span v-if="note.created_at"> - {{ formatNoteDate(note.created_at) }}</span>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </b-card-text>
            </b-tab>
            <!-- Past Assignments Tab  -->
            <b-tab title="Past Assignments" @click="loadAssignments('past')">
              <b-card-text>
                <div class="action-buttons mb-3">
                  <button type="button" class="btn btn-info btn-sm mr-2" @click="expandAll">
                    <i class="fa fa-expand"></i> Expand All
                  </button>
                  <button type="button" class="btn btn-secondary btn-sm mr-2" @click="collapseAll">
                    <i class="fa fa-compress"></i> Collapse All
                  </button>
                  <button type="button" class="btn btn-success btn-sm" @click="loadAssignments('past')">
                    <i class="fa fa-refresh"></i> Refresh
                  </button>
                </div>
                <div v-if="assignments.length === 0 && !isLoading" class="text-center py-4">
                  <p>No past assignments found.</p>
                </div>
                <div v-else class="assignments-list">
                  <div v-for="(assignment, index) in assignments" :key="assignment.id" class="assignment-row">
                    <div class="assignment-header" @click="toggleExpand(index)">
                      <div class="row">
                        <div class="col-3"><strong>{{ assignment.date_formatted }}</strong></div>
                        <div class="col-3">
                          <span v-if="getUserCustomTime(assignment)" style="color: #ff9800; font-weight: bold;">
                            <i class="fa fa-clock-o"></i> {{ getUserCustomTime(assignment) }}
                          </span>
                          <span v-else>
                            {{ assignment.call_time }}
                          </span>
                        </div>
                        <div class="col-5">{{ assignment.details }} ({{ assignment.status }})</div>
                        <div class="col-1 text-right">
                          <i :class="expandedRows.includes(index) ? 'fa fa-caret-down' : 'fa fa-caret-right'"></i>
                        </div>
                      </div>
                    </div>
                    <div v-if="expandedRows.includes(index)" class="assignment-details">
                      <div class="detail-section" v-if="assignment.people && assignment.people.length > 0">
                        <strong>People ({{ assignment.people.length }})</strong>
                        <ul class="detailed-list">
                          <li v-for="person in assignment.people" :key="person.id">
                            <div class="person-main-info">
                              {{ person.name }}
                              <template v-if="person.individual_start_time && person.individual_end_time">
                                - <strong>{{ formatTime(person.individual_start_time) }} - {{
                                  formatTime(person.individual_end_time) }}</strong>
                              </template>
                              <template v-if="person.email"> - {{ person.email }}</template>
                              <template v-if="person.phone"> - {{ person.phone }}</template>
                              <template v-if="person.mobile && person.mobile !== person.phone"> - {{ person.mobile
                              }}</template>
                            </div>
                            <ul v-if="person.info && person.info.Credentials && person.info.Credentials !== 'N/A'"
                              class="credentials-list">
                              <li v-for="(cred, idx) in parseCredentials(person.info.Credentials)" :key="idx">{{ cred }}
                              </li>
                            </ul>
                          </li>
                        </ul>
                      </div>
                      <div class="detail-section" v-if="assignment.assets && assignment.assets.length > 0">
                        <strong>Assets ({{ assignment.assets.length }})</strong>
                        <ul class="detailed-list">
                          <li v-for="asset in assignment.assets" :key="asset.id">{{ asset.alias || asset.name ||
                            asset.id }}</li>
                        </ul>
                      </div>
                      <div class="detail-section" v-if="assignment.jobs && assignment.jobs.length > 0">
                        <strong>Jobs ({{ assignment.jobs.length }})</strong>
                        <ul class="detailed-list">
                          <li v-for="job in assignment.jobs" :key="job.id">
                            <div class="job-name">{{ job.name }}</div>
                            <ul v-if="hasJobContacts(job)" class="contacts-list">
                              <li v-if="job.site_name" class="site-address-line">{{ job.site_name }}</li>
                              <li v-if="job.site_address_1" class="site-address-line">{{ job.site_address_1 }}</li>
                              <li v-if="job.site_address_2" class="site-address-line">{{ job.site_address_2 }}</li>
                              <li v-if="job.site_city || job.site_state || job.site_zip" class="site-address-line">{{
                                [job.site_city, job.site_state, job.site_zip].filter(Boolean).join(', ') }}</li>
                              <li
                                v-if="job.people && job.people.primary_contact && job.people.primary_contact.full_name">
                                Primary Contact - {{ formatContactInfo(job.people.primary_contact) }}
                              </li>
                              <li v-if="job.people && job.people.estimator && job.people.estimator.full_name">
                                Estimator - {{ formatContactInfo(job.people.estimator) }}
                              </li>
                              <li
                                v-if="job.people && job.people.project_manager && job.people.project_manager.full_name">
                                Project Manager - {{ formatContactInfo(job.people.project_manager) }}
                              </li>
                              <li v-if="job.people && job.people.foreman && job.people.foreman.full_name">
                                Foreman - {{ formatContactInfo(job.people.foreman) }}
                              </li>
                              <li v-if="job.people && job.people.accountant && job.people.accountant.full_name">
                                Accountant - {{ formatContactInfo(job.people.accountant) }}
                              </li>
                            </ul>
                          </li>
                        </ul>
                      </div>
                      <div class="detail-section" v-if="assignment.notes && assignment.notes.length > 0">
                        <strong>Crew Notes ({{ assignment.notes.length }})</strong>
                        <ul class="detailed-list notes-list">
                          <li v-for="note in assignment.notes" :key="note.id">
                            <div class="note-item">
                              <span v-if="note.activity" class="note-activity"><strong>{{ note.activity }}</strong>:
                              </span>
                              <span class="note-text">{{ note.full_note || note.text }}</span>
                            </div>
                            <div v-if="note.created_by_user || note.created_at" class="note-meta">
                              <span v-if="note.created_by_user">{{ note.created_by_user }}</span>
                              <span v-if="note.created_at"> - {{ formatNoteDate(note.created_at) }}</span>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </b-card-text>
            </b-tab>
          </b-tabs>
        </b-card>
      </div>
    </div>

    <!-- Add Timesheet Modal goes here -->

    <b-modal id="add-timesheet-modal" ref="addTimesheetModal" title="Add Job To Timesheet" hide-footer>
      <add-time-sheet-modal :key="selectedJobForTimesheet ? selectedJobForTimesheet.id : 'new'"
        :prefill-job-id="selectedJobForTimesheet ? selectedJobForTimesheet.id : null"
        :prefill-job-name="selectedJobForTimesheet ? selectedJobForTimesheet.name : ''" @saved="onTimesheetSaved" />
    </b-modal>

  </div>
</template>

<script>
import CommonConstants from '@/constants/constants.js'
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import Vue from 'vue';
import AddTimeSheetModal from './AddTimesheetModal.vue';

export default {
  name: 'Assignments',
  components: { Loading, AddTimeSheetModal },
  data() {
    return {
      constants: CommonConstants,
      header: {},
      userData: {},
      assignments: [],
      expandedRows: [],
      isLoading: false,
      currentType: 'future',
      selectedJobForTimesheet: null,
    }
  },
  mounted: function () {
    let self = this;
    self.header = {
      'Content-type': "application/json",
      'token': localStorage.getItem('token')
    }
    self.userData = JSON.parse(localStorage.getItem('userData'))
    self.loadAssignments('future');
  },
  methods: {
    // show the timesheet modal function

    openTimesheetModal: function (job) {
      // console.log('Timesheet modal opened');
      this.selectedJobForTimesheet = job;
      this.$refs.addTimesheetModal.show();
    },

    onTimesheetSaved() {
      this.$refs.addTimesheetModal.hide();

      // Optional: refresh assignments after creating the timesheet
      this.loadAssignments(this.currentType);
    },

    loadAssignments: function (type) {
      let self = this;
      self.currentType = type;
      self.isLoading = true;
      self.assignments = [];
      self.expandedRows = [];

      axios.get(self.constants.api_url + 'lineup/my-assignments?type=' + type, { headers: self.header })
        .then(function (response) {
          if (response.data.flag) {
            self.assignments = response.data.data;
          } else {
            Vue.$toast.error(response.data.message || 'Failed to load assignments', { position: 'top' });
          }
        })
        .catch(function (error) {
          Vue.$toast.error('Error loading assignments', { position: 'top' });
          console.error(error);
        })
        .finally(function () {
          self.isLoading = false;
        });
    },
    toggleExpand: function (index) {
      let self = this;
      const idx = self.expandedRows.indexOf(index);
      if (idx > -1) {
        self.expandedRows.splice(idx, 1);
      } else {
        self.expandedRows.push(index);
      }
    },
    expandAll: function () {
      let self = this;
      self.expandedRows = self.assignments.map((_, index) => index);
    },
    collapseAll: function () {
      let self = this;
      self.expandedRows = [];
    },
    formatTime: function (timeStr) {
      if (!timeStr) return '';
      const parts = timeStr.split(':');
      let hours = parseInt(parts[0], 10);
      const minutes = parts[1] || '00';
      let period = 'AM';
      if (hours === 0) { hours = 12; }
      else if (hours === 12) { period = 'PM'; }
      else if (hours > 12) { hours = hours - 12; period = 'PM'; }
      return hours + ':' + minutes + ' ' + period;
    },
    parseCredentials: function (credStr) {
      if (!credStr || credStr === 'N/A') return [];
      return credStr.split(',').map(function (c) { return c.trim(); }).filter(Boolean);
    },
    hasJobContacts: function (job) {
      if (!job.people) return false;
      var roles = ['primary_contact', 'estimator', 'project_manager', 'foreman', 'accountant'];
      for (var i = 0; i < roles.length; i++) {
        if (job.people[roles[i]] && job.people[roles[i]].full_name) return true;
      }
      return false;
    },
    formatContactInfo: function (contact) {
      if (!contact) return '';
      var parts = [contact.full_name];
      if (contact.email) parts.push(contact.email);
      if (contact.phone) parts.push(contact.phone);
      if (contact.mobile && contact.mobile !== contact.phone) parts.push(contact.mobile);
      return parts.join(' - ');
    },
    formatNoteDate: function (dateStr) {
      if (!dateStr) return '';
      var date = new Date(dateStr);
      var month = date.getMonth() + 1;
      var day = date.getDate();
      var year = date.getFullYear();
      var hours = date.getHours();
      var minutes = date.getMinutes();
      var period = 'AM';
      if (hours === 0) { hours = 12; }
      else if (hours === 12) { period = 'PM'; }
      else if (hours > 12) { hours = hours - 12; period = 'PM'; }
      return month + '/' + day + '/' + year + ' ' + hours + ':' + (minutes < 10 ? '0' : '') + minutes + ' ' + period;
    },
    getUserCustomTime: function (assignment) {
      let self = this;
      if (!self.userData || !self.userData.id || !assignment.people) {
        return null;
      }
      const myPerson = assignment.people.find(p => p.id === self.userData.id);
      if (myPerson && myPerson.individual_start_time) {
        return self.formatTime(myPerson.individual_start_time);
      }
      return null;
    }
  }
}
</script>

<style scoped>
.page-title {
  margin-bottom: 20px;
  font-weight: 600;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
}

.assignments-list {
  border: 1px solid #dee2e6;
  border-radius: 4px;
}

.assignment-row {
  border-bottom: 1px solid #dee2e6;
}

.assignment-row:last-child {
  border-bottom: none;
}

.assignment-header {
  padding: 12px 15px;
  cursor: pointer;
  background-color: #f8f9fa;
}

.assignment-header:hover {
  background-color: #e9ecef;
}

.assignment-header .row {
  flex-wrap: nowrap !important;
}

.assignment-details {
  padding: 15px;
  background-color: #fff;
  border-top: 1px solid #dee2e6;
}

.detail-section {
  margin-bottom: 15px;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.detail-section ul {
  margin: 5px 0 0 0;
  padding-left: 20px;
}

.detail-section li {
  margin-bottom: 3px;
}

.detailed-list {
  list-style-type: disc;
  padding-left: 25px;
  margin: 5px 0;
}

.credentials-list,
.contacts-list {
  list-style-type: none;
  padding-left: 20px;
  margin: 3px 0 10px 0;
  font-size: 0.9em;
  color: #555;
}

.person-main-info,
.job-name {
  margin-bottom: 2px;
}

.notes-list li {
  margin-bottom: 10px;
  background: #fffbf0;
  padding: 8px 10px;
  border-radius: 4px;
  border-left: 3px solid #f0ad4e;
}

.note-item {
  margin-bottom: 3px;
}

.note-activity {
  color: #333;
}

.note-text {
  color: #555;
}

.note-meta {
  font-size: 0.85em;
  color: #888;
  font-style: italic;
}
</style>
